import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { wellnessAreaService, wellnessHomeworkService } from '../services/api';

const PILLAR_COLOR_VARIANT = {
  mind: 'purple',
  move: 'emerald',
  journal: 'coral',
  faith: 'amber',
};

const PILLAR_BADGE_CLASSES = {
  mind: 'bg-purple-100 text-purple-800 border-purple-200',
  move: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  journal: 'bg-coral-100 text-coral-800 border-coral-200',
  faith: 'bg-amber-100 text-amber-800 border-amber-200',
};

const DIFFICULTY_BADGE_VARIANT = {
  foundational: 'success',
  intermediate: 'primary',
  advanced: 'warning',
};

const PILLAR_OPTIONS = ['mind', 'move', 'journal', 'faith'];
const DIFFICULTY_OPTIONS = ['foundational', 'intermediate', 'advanced'];
const PHASE_OPTIONS = [1, 2, 3, 4];
const PHASE_LABELS = { 1: 'Phase 1 · Stabilize', 2: 'Phase 2 · Understand', 3: 'Phase 3 · Rebuild', 4: 'Phase 4 · Sustain' };

const PillarBadge = ({ pillar }) => {
  if (!pillar) return null;
  const classes =
    PILLAR_BADGE_CLASSES[pillar] ||
    'bg-bgSecondary text-textSecondary border-border';
  return (
    <span
      className={`inline-flex items-center font-medium rounded-full px-3 py-1 text-sm border capitalize ${classes}`}
    >
      {pillar}
    </span>
  );
};

const HomeworkLibrary = () => {
  const { user } = useAuth();

  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(true);

  const [filters, setFilters] = useState({
    wellnessAreaSlug: '',
    pillar: '',
    difficulty: '',
    phase: user?.currentPhase || '',
  });

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [assigningId, setAssigningId] = useState(null);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [assignErrors, setAssignErrors] = useState({});

  const excludeFaith = user?.homeworkPreferences?.excludeFaith === true;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setAreasLoading(true);
        const response = await wellnessAreaService.list();
        if (cancelled) return;
        const data = response?.data?.data || response?.data || [];
        setAreas(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setAreas([]);
      } finally {
        if (!cancelled) setAreasLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildParams = useCallback(
    (pageNum) => {
      const params = { page: pageNum };
      if (filters.wellnessAreaSlug) params.wellnessArea = filters.wellnessAreaSlug;
      if (filters.pillar) params.pillar = filters.pillar;
      if (filters.difficulty) params.difficulty = filters.difficulty;
      if (filters.phase) params.phase = filters.phase;
      return params;
    },
    [filters]
  );

  const fetchPage = useCallback(
    async (pageNum, append) => {
      try {
        setLoading(true);
        setError(null);
        const response = await wellnessHomeworkService.getLibrary(
          buildParams(pageNum)
        );
        const payload = response?.data || {};
        const nextItems =
          payload.data || payload.items || payload.results || [];
        const pagination = payload.pagination || {};
        const totalPages =
          pagination.totalPages ?? pagination.pages ?? null;
        const more =
          typeof pagination.hasMore === 'boolean'
            ? pagination.hasMore
            : totalPages != null
              ? pageNum < totalPages
              : Array.isArray(nextItems) && nextItems.length > 0;

        setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setHasMore(more);
        setPage(pageNum);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to load homework library.'
        );
        if (!append) setItems([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, false);
  }, [filters, fetchPage]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      wellnessAreaSlug: '',
      pillar: '',
      difficulty: '',
      phase: user?.currentPhase || '',
    });
  };

  const handleAssignSelf = async (item) => {
    if (!item?._id) return;
    try {
      setAssigningId(item._id);
      setAssignErrors((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
      await wellnessHomeworkService.assignSelf({
        homeworkItemId: item._id,
        wellnessAreaId: item.primaryArea?._id,
      });
      setAssignedIds((prev) => {
        const next = new Set(prev);
        next.add(item._id);
        return next;
      });
    } catch (err) {
      setAssignErrors((prev) => ({
        ...prev,
        [item._id]:
          err?.response?.data?.message ||
          err?.message ||
          'Unable to assign this exercise.',
      }));
    } finally {
      setAssigningId(null);
    }
  };

  const availableAreas = useMemo(() => areas.slice().sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '')
  ), [areas]);

  return (
    <div className="min-h-screen bg-bgPrimary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="text-primary hover:underline text-sm"
          >
            &larr; Back to dashboard
          </Link>
          <h1 className="mt-2 text-3xl font-bold text-textPrimary">
            Homework Library
          </h1>
          <p className="text-textSecondary mt-1">
            Browse exercises and add the ones that resonate to your own plan.
          </p>
        </div>

        {excludeFaith && (
          <div className="mb-4 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-sm">
            Faith-integrated content is hidden based on your preferences.
            Update in profile settings.
          </div>
        )}

        <div className="sticky top-0 z-10 bg-bgPrimary/95 backdrop-blur border-b border-border py-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">
                Focus area
              </label>
              <select
                value={filters.wellnessAreaSlug}
                onChange={(e) =>
                  handleFilterChange('wellnessAreaSlug', e.target.value)
                }
                disabled={areasLoading}
                className="w-full px-3 py-2 rounded-md border border-border bg-surface text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">All focus areas</option>
                {availableAreas.map((a) => (
                  <option key={a._id || a.slug} value={a.slug}>
                    {a.displayName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">
                Pillar
              </label>
              <select
                value={filters.pillar}
                onChange={(e) => handleFilterChange('pillar', e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-surface text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize"
              >
                <option value="">All pillars</option>
                {PILLAR_OPTIONS.map((p) => (
                  <option key={p} value={p} className="capitalize">
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">
                Difficulty
              </label>
              <select
                value={filters.difficulty}
                onChange={(e) =>
                  handleFilterChange('difficulty', e.target.value)
                }
                className="w-full px-3 py-2 rounded-md border border-border bg-surface text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize"
              >
                <option value="">Any difficulty</option>
                {DIFFICULTY_OPTIONS.map((d) => (
                  <option key={d} value={d} className="capitalize">
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">
                Phase
              </label>
              <select
                value={filters.phase}
                onChange={(e) => handleFilterChange('phase', e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-surface text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize"
              >
                <option value="">Any phase</option>
                {PHASE_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {PHASE_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <Button
                variant="secondary"
                size="md"
                onClick={handleClearFilters}
                fullWidth
              >
                Reset filters
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md border border-error/20 bg-error/10 text-error text-sm">
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="rounded-lg border border-border bg-surface p-6 animate-pulse"
              >
                <div className="h-5 bg-bgSecondary rounded w-2/3 mb-3" />
                <div className="h-4 bg-bgSecondary rounded w-1/2 mb-2" />
                <div className="h-4 bg-bgSecondary rounded w-full mb-2" />
                <div className="h-4 bg-bgSecondary rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : items.length === 0 && !loading ? (
          <div className="p-10 rounded-lg border border-border bg-surface text-center">
            <p className="text-textSecondary">
              No matching exercises. Try a different filter.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => {
                const pillar = item.pillar;
                const colorVariant = PILLAR_COLOR_VARIANT[pillar] || null;
                const isAssigned = assignedIds.has(item._id);
                const isAssigning = assigningId === item._id;
                const assignError = assignErrors[item._id];

                return (
                  <Card
                    key={item._id}
                    variant="default"
                    colorVariant={colorVariant}
                    padding="default"
                    borderAccent={Boolean(colorVariant)}
                    hover
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-textPrimary leading-snug">
                        {item.title}
                      </h3>
                      <PillarBadge pillar={pillar} />
                    </div>

                    {item.primaryArea?.displayName && (
                      <p className="mt-2 text-sm text-textSecondary">
                        {item.primaryArea.displayName}
                      </p>
                    )}

                    {item.description && (
                      <p className="mt-2 text-sm text-textMuted line-clamp-3">
                        {item.description}
                      </p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {item.estimatedMinutes != null && (
                        <Badge variant="neutral" size="sm">
                          {item.estimatedMinutes} min
                        </Badge>
                      )}
                      {item.difficultyLevel && (
                        <Badge
                          variant={
                            DIFFICULTY_BADGE_VARIANT[item.difficultyLevel] ||
                            'neutral'
                          }
                          size="sm"
                        >
                          <span className="capitalize">
                            {item.difficultyLevel}
                          </span>
                        </Badge>
                      )}
                      {item.isFaithIntegrated && (
                        <span className="inline-flex items-center font-medium rounded-full px-2 py-0.5 text-xs border bg-amber-50 text-amber-800 border-amber-200">
                          Faith
                        </span>
                      )}
                    </div>

                    <div className="mt-4">
                      {isAssigned ? (
                        <p className="text-sm text-emerald-700 font-medium">
                          Added to your homework
                        </p>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleAssignSelf(item)}
                          disabled={isAssigning}
                        >
                          {isAssigning ? 'Adding...' : 'Assign to Myself'}
                        </Button>
                      )}
                      {assignError && (
                        <p className="mt-2 text-sm text-error">
                          {assignError}
                        </p>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {hasMore && (
              <div className="mt-6 flex justify-center">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => fetchPage(page + 1, true)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HomeworkLibrary;
